/**
 * Application entry point.
 *
 * Order matters:
 *   1. Import the AMD design-system stylesheet ONCE. This defines the real design
 *      tokens on :root (--amd-teal, --ink-*, --tx*, --s-*, --t-*, --r-*, --el-*),
 *      ships the Klavika @font-face rules, and styles every library component.
 *   2. Import global base styles, which @imports tokens.bridge.css - the aliases
 *      that map this app's semantic token names onto the library tokens above.
 *      It MUST come after the library import so the library :root exists first.
 *   3. Mount the app inside all providers.
 */
import '@amd-deae-ceme/design-system/styles.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/global.css';

import App from './App';
import { AppProviders } from './app/providers/AppProviders';
import { store } from './app/store';
import { installTooltipEdgeGuard } from './lib/tooltipEdgeGuard';
import { preloadCurrentRoute } from './router/preloadCurrentRoute';
import { selectIsAuthenticated } from './security/auth/authSlice';
import { bootstrapAuth } from './security/auth/bootstrapAuth';
import { getVariant } from './variant/appMode';

// The tab title for this product, first thing — index.html has none, so the
// tab never shows a generic title before the real one (the product is known
// only at runtime, from the host).
const { mode, documentTitle } = getVariant();
if (documentTitle) document.title = documentTitle;

// Settle auth before the first render, so RequireAuth can decide (the sign-in
// screen vs. the page) at once.
bootstrapAuth(store);

// Every tooltip bubble, app-wide, is kept inside the window (see the module).
installTooltipEdgeGuard();

// Draw only once the first screen's page code is here, so the first paint is
// the real page — never a "Loading…" placeholder (see the module). The promise
// never rejects: a failed download still renders, and the router reports it.
preloadCurrentRoute(mode, { signedIn: selectIsAuthenticated(store.getState()) }).then(() => {
  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <AppProviders>
        <App />
      </AppProviders>
    </StrictMode>,
  );
});
